import { useEffect, useMemo, useState } from "react";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "http://127.0.0.1:8000";

const documentLabels = {
  NATIONAL_ID: "National ID",
  PASSPORT: "Passport",
  BUSINESS_REGISTRATION: "Certificate of incorporation",
  TAX_CLEARANCE: "Tax clearance",
  OTHER: "Other supporting document",
};

function normaliseMedia(src) {
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  if (src.startsWith("/media/")) return `${API_ORIGIN}${src}`;
  if (src.startsWith("media/")) return `${API_ORIGIN}/${src}`;
  return src;
}

function Modal({ title, eyebrow, children, onClose }) {
  return (
    <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
      <section className="tazy-modal-card seller-modal-card">
        <header className="tazy-modal-header">
          <div>
            <span>{eyebrow}</span>
            <h3>{title}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close modal">×</button>
        </header>
        <div className="tazy-modal-body">{children}</div>
      </section>
    </div>
  );
}

function statusClass(status) {
  const value = String(status || "").toUpperCase();
  if (["APPROVED", "VERIFIED", "ACTIVE", "PUBLISHED"].includes(value)) return "success";
  if (["REJECTED", "SUSPENDED", "EXPIRED"].includes(value)) return "danger";
  return "warning";
}

export default function SellerWorkspace() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [activeModal, setActiveModal] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [docForm, setDocForm] = useState({ document_type: "NATIONAL_ID", title: "", document_number: "", expiry_date: "", file: null });

  const canUseSellerWorkspace = ["SELLER", "BUSINESS"].includes(user?.account_type) || user?.is_platform_staff;

  async function loadWorkspace() {
    setLoading(true);
    setError("");
    try {
      const res = await phase1Api.sellerWorkspace();
      setWorkspace(res.data);
      const profile = res.data.profile || {};
      setProfileForm({
        store_name: profile.store_name || "",
        contact_phone: profile.contact_phone || "",
        description: profile.description || "",
        business_address: profile.business_address || "",
        city: profile.city || "",
        province: profile.province || "",
        years_operating: profile.years_operating || "",
        whatsapp_number: profile.whatsapp_number || "",
        pickup_location: profile.pickup_location || "",
        service_area_summary: profile.service_area_summary || "",
        response_time_note: profile.response_time_note || "",
        accepts_whatsapp_enquiries: profile.accepts_whatsapp_enquiries !== false,
        accepts_pickup: profile.accepts_pickup !== false,
        accepts_delivery_enquiries: profile.accepts_delivery_enquiries !== false,
        store_logo: null,
      });
      setLogoPreview(normaliseMedia(profile.store_logo_src || ""));
      setSelectedCategories((profile.categories || []).map((item) => item.id));
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load seller workspace.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadWorkspace(); }, []);

  const readiness = workspace?.verification || { missing: [], required: [], approved: [] };
  const profile = workspace?.profile || {};
  const documents = workspace?.documents || [];
  const categories = workspace?.categories || [];

  const progress = useMemo(() => {
    const required = readiness.required?.length || 0;
    const approved = readiness.approved?.filter((item) => readiness.required?.includes(item)).length || 0;
    return required ? Math.round((approved / required) * 100) : 0;
  }, [readiness]);

  const categoryNames = (profile.categories || []).map((category) => category.name).join(", ") || "No selling categories selected yet";
  const sellerInitial = String(profile.store_name || user?.display_name || "T").trim().charAt(0).toUpperCase();

  function updateField(name, value) {
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleLogoChange(file) {
    updateField("store_logo", file || null);
    setLogoPreview(file ? URL.createObjectURL(file) : normaliseMedia(profile.store_logo_src || ""));
  }

  function toggleCategory(categoryId) {
    setSelectedCategories((prev) => prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]);
  }

  async function saveProfile(event) {
    if (event?.preventDefault) event.preventDefault();
    setMessage("");
    setError("");
    try {
      const payload = new FormData();
      Object.entries(profileForm).forEach(([key, value]) => {
        if (key === "store_logo") {
          if (value) payload.append("store_logo", value);
        } else if (value !== null && value !== undefined) {
          payload.append(key, value);
        }
      });
      selectedCategories.forEach((id) => payload.append("category_ids", id));
      await phase1Api.updateSellerWorkspace(payload);
      setMessage("Store profile updated successfully.");
      setActiveModal(null);
      await loadWorkspace();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save store profile. Check required fields.");
    }
  }

  async function uploadDocument(event) {
    event.preventDefault();
    setMessage("");
    setError("");
    if (!docForm.file) {
      setError("Attach the actual document file before submitting.");
      return;
    }
    const payload = new FormData();
    payload.append("document_type", docForm.document_type);
    payload.append("title", docForm.title || documentLabels[docForm.document_type] || docForm.document_type);
    if (docForm.document_number) payload.append("document_number", docForm.document_number);
    if (docForm.expiry_date) payload.append("expiry_date", docForm.expiry_date);
    payload.append("file", docForm.file);
    try {
      await phase1Api.createDocument(payload);
      setDocForm({ document_type: "NATIONAL_ID", title: "", document_number: "", expiry_date: "", file: null });
      setMessage("Document uploaded. It will count for verification after admin review and approval.");
      setActiveModal(null);
      await loadWorkspace();
    } catch (err) {
      const data = err?.response?.data || {};
      setError(data.file || data.detail || "Document upload failed. Select a document type and attach the correct file.");
    }
  }

  if (!canUseSellerWorkspace) {
    return <div className="portal-shell-v2"><PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} /><main className="portal-main-v2"><PortalTopbar user={user} onLogout={logout} /><section className="workspace-card"><h2>Seller workspace unavailable</h2><p>This area is for sellers and business merchants only.</p></section></main></div>;
  }

  return (
    <div className={`portal-shell-v2 ${collapsed ? "sidebar-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <main className="portal-main-v2 workspace-main compact-workspace-main seller-workspace-polished">
        <PortalTopbar user={user} onLogout={logout} />
        <section className="workspace-hero compact-workspace-hero seller-hero-clean">
          <div className="seller-identity-block">
            <div className="seller-logo-avatar">
              {logoPreview ? <img src={logoPreview} alt="Store logo" /> : <span>{sellerInitial}</span>}
            </div>
            <div>
              <span>MERCHANT WORKSPACE</span>
              <h1>My Store</h1>
              <p>Store setup, selling focus and verification only. Product catalogue is managed under Products.</p>
            </div>
          </div>
          <div className="verification-meter compact-meter">
            <strong>{progress}%</strong>
            <span>Verification readiness</span>
          </div>
        </section>

        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}

        {loading ? <div className="workspace-card">Loading seller workspace...</div> : (
          <div className="seller-workspace-cards seller-workspace-cards--clean">
            <section className="workspace-card merchant-summary-card merchant-profile-card">
              <div className="workspace-section-title"><span>STORE PROFILE</span><h2>{profile.store_name || "Store profile"}</h2></div>
              <p>{profile.description || "Add a clear description explaining the store, products and market served."}</p>
              <dl className="profile-summary-grid">
                <div><dt>Contact</dt><dd>Managed through TAZY CART</dd></div>
                <div><dt>Pickup point</dt><dd>{profile.pickup_location || "—"}</dd></div>
                <div><dt>City</dt><dd>{profile.city || "—"}</dd></div>
                <div><dt>Province</dt><dd>{profile.province || "—"}</dd></div>
                <div><dt>Years operating</dt><dd>{profile.years_operating || "—"}</dd></div>
              </dl>
              <button type="button" className="secondary-workspace-btn" onClick={() => setActiveModal("profile")}>Edit profile and logo</button>
            </section>

            <section className="workspace-card merchant-summary-card">
              <div className="workspace-section-title"><span>SELLING FOCUS</span><h2>Product categories</h2></div>
              <p className="soft-text">Select every category this seller may trade in. Riskier categories can still be reviewed by the platform.</p>
              <div className="category-summary-pill">{categoryNames}</div>
              <button type="button" className="secondary-workspace-btn" onClick={() => setActiveModal("categories")}>Manage categories</button>
            </section>

            <section className="workspace-card merchant-summary-card seller-tools-card">
              <div className="workspace-section-title"><span>SELLER TOOLS</span><h2>Trading readiness</h2></div>
              <p className="soft-text">Useful seller controls for protected platform enquiries, pickup support and delivery enquiries.</p>
              <div className="seller-tool-grid">
                <div><strong>ON</strong><span>TAZY CART enquiries</span></div>
                <div><strong>{profile.accepts_pickup !== false ? "ON" : "OFF"}</strong><span>Buyer pickup</span></div>
                <div><strong>{profile.accepts_delivery_enquiries !== false ? "ON" : "OFF"}</strong><span>Delivery enquiries</span></div>
                <div><strong>{profile.response_time_note || "SET"}</strong><span>Response target</span></div>
              </div>
              <button type="button" className="secondary-workspace-btn" onClick={() => setActiveModal("profile")}>Update trading controls</button>
            </section>

            <section className="workspace-card merchant-summary-card verification-card-clean">
              <div className="workspace-section-title"><span>TRUST REVIEW</span><h2>Verification status</h2></div>
              <div className={`status-pill big ${readiness.is_ready ? "success" : "warning"}`}>{readiness.is_ready ? "Ready for admin approval" : "Documents pending"}</div>
              <div className="verification-list compact-verification-list">
                {(readiness.required || []).map((docType) => (
                  <div key={docType} className={readiness.approved?.includes(docType) ? "done" : "missing"}>
                    <b>{readiness.approved?.includes(docType) ? "✓" : "!"}</b>
                    <span>{documentLabels[docType] || docType}</span>
                  </div>
                ))}
              </div>
              <p className="verification-note">Documents must be opened and approved by admin before seller verification can proceed.</p>
            </section>

            <section className="workspace-card merchant-summary-card document-control-card">
              <div className="workspace-section-title"><span>DOCUMENTS</span><h2>Verification documents</h2></div>
              <button type="button" className="secondary-workspace-btn" onClick={() => setActiveModal("document")}>Upload document</button>
              <div className="document-list-mini document-status-list">
                {documents.length === 0 ? <p className="soft-text">No documents uploaded yet.</p> : documents.map((doc) => (
                  <div key={doc.id}>
                    <strong>{doc.document_type_label || documentLabels[doc.document_type] || doc.document_type}</strong>
                    <span className={`status-chip ${statusClass(doc.status)}`}>{doc.status_label || doc.status}</span>
                    {doc.file_url ? <a href={normaliseMedia(doc.file_url)} target="_blank" rel="noreferrer">Open</a> : <small>No file</small>}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </main>

      {activeModal === "profile" && (
        <Modal title="Edit store profile" eyebrow="STORE SETUP" onClose={() => setActiveModal(null)}>
          <form onSubmit={saveProfile} className="workspace-form modal-form-grid">
            <div className="full logo-upload-row">
              <label>Business / store logo</label>
              <div className="logo-upload-preview">
                <div>{logoPreview ? <img src={logoPreview} alt="Logo preview" /> : <span>{sellerInitial}</span>}</div>
                <input type="file" accept="image/*" onChange={(e) => handleLogoChange(e.target.files?.[0] || null)} />
              </div>
            </div>
            <div><label>Store / trading name</label><input value={profileForm.store_name} onChange={(e) => updateField("store_name", e.target.value)} required /></div>
            <div><label>Operational phone (private to TAZY CART)</label><input value={profileForm.contact_phone} onChange={(e) => updateField("contact_phone", e.target.value)} placeholder="+263..." /></div>
            <div className="full"><label>Store description</label><textarea value={profileForm.description} onChange={(e) => updateField("description", e.target.value)} placeholder="What do you sell and who do you serve?" rows="4" /></div>
            <div><label>Response time target</label><input value={profileForm.response_time_note || ""} onChange={(e) => updateField("response_time_note", e.target.value)} placeholder="Example: Within 2 hours" /></div>
            <div className="full"><label>Pickup location</label><input value={profileForm.pickup_location || ""} onChange={(e) => updateField("pickup_location", e.target.value)} placeholder="Shop, warehouse or pickup point" /></div>
            <div className="full"><label>Service area summary</label><input value={profileForm.service_area_summary || ""} onChange={(e) => updateField("service_area_summary", e.target.value)} placeholder="Example: Harare CBD, Bulawayo, nationwide delivery enquiries" /></div>
            <label className="modal-check"><input type="checkbox" checked={profileForm.accepts_pickup !== false} onChange={(e) => updateField("accepts_pickup", e.target.checked)} /> Allow buyer pickup</label>
            <label className="modal-check full"><input type="checkbox" checked={profileForm.accepts_delivery_enquiries !== false} onChange={(e) => updateField("accepts_delivery_enquiries", e.target.checked)} /> Accept delivery enquiries</label>
            <div className="full"><label>Business address</label><input value={profileForm.business_address} onChange={(e) => updateField("business_address", e.target.value)} /></div>
            <div><label>City</label><input value={profileForm.city} onChange={(e) => updateField("city", e.target.value)} /></div>
            <div><label>Province</label><input value={profileForm.province} onChange={(e) => updateField("province", e.target.value)} /></div>
            <div><label>Years operating</label><input type="number" min="0" value={profileForm.years_operating} onChange={(e) => updateField("years_operating", e.target.value)} /></div>
            <button type="submit">Save profile</button>
          </form>
        </Modal>
      )}

      {activeModal === "categories" && (
        <Modal title="Manage selling categories" eyebrow="SELLING FOCUS" onClose={() => setActiveModal(null)}>
          <p className="soft-text">Select all categories this seller may trade in. Restricted/risky categories can still be moderated later.</p>
          <div className="category-check-grid modal-category-grid">
            {categories.map((category) => (
              <label key={category.id} className={selectedCategories.includes(category.id) ? "selected" : ""}>
                <input type="checkbox" checked={selectedCategories.includes(category.id)} onChange={() => toggleCategory(category.id)} />
                <span>{category.name}</span>
              </label>
            ))}
          </div>
          <button type="button" onClick={saveProfile} className="secondary-workspace-btn">Save categories</button>
        </Modal>
      )}

      {activeModal === "document" && (
        <Modal title="Upload verification document" eyebrow="DOCUMENT REVIEW" onClose={() => setActiveModal(null)}>
          <form onSubmit={uploadDocument} className="workspace-form modal-form-grid">
            <div><label>Document type</label><select value={docForm.document_type} onChange={(e) => setDocForm((prev) => ({ ...prev, document_type: e.target.value }))}>
              <option value="NATIONAL_ID">National ID</option>
              <option value="PASSPORT">Passport</option>
              <option value="BUSINESS_REGISTRATION">Certificate of incorporation</option>
              <option value="TAX_CLEARANCE">Tax clearance</option>
              <option value="OTHER">Other supporting document</option>
            </select></div>
            <div><label>Document number</label><input value={docForm.document_number} onChange={(e) => setDocForm((prev) => ({ ...prev, document_number: e.target.value }))} /></div>
            <div className="full"><label>Document title</label><input value={docForm.title} onChange={(e) => setDocForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="Example: Company tax clearance 2026" /></div>
            <div><label>Expiry date</label><input type="date" value={docForm.expiry_date} onChange={(e) => setDocForm((prev) => ({ ...prev, expiry_date: e.target.value }))} /></div>
            <div><label>File</label><input type="file" required accept="image/*,application/pdf" onChange={(e) => setDocForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))} /></div>
            <button type="submit">Upload document</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
